/**
 * Utility functions for Comics application
 */

/**
 * Constant for identifying variant comics
 */
export const VARIANT_IDENTIFIER = '(Variant)';

/**
 * Check if an item has a valid image that is not a placeholder
 * @param {object} item - The comic/series/hero object
 * @returns {boolean} - True if the item has a valid image
 */
export const hasValidImage = (item) => {
    // Check for Comic Vine image
    if (item.comicVineImage) {
        if (typeof item.comicVineImage === 'string' && item.comicVineImage.length > 0) {
            return true;
        }
        if (typeof item.comicVineImage === 'object' && item.comicVineImage !== null) {
            return !!(item.comicVineImage.small_url || item.comicVineImage.medium_url || item.comicVineImage.screen_url);
        }
    }
    
    // Check for thumbnail
    if (item.thumbnail) {
        const path = item.thumbnail.path;
        if (typeof path === 'string' && path.length > 0) {
            // Check for placeholder image
            if (path.includes('image_not_available') || path.includes('4c002e0305708')) {
                return false;
            }
            return true;
        }
        if (typeof path === 'object' && path !== null) {
            return !!(path.small_url || path.medium_url || path.screen_url);
        }
    }
    
    return false;
};

/**
 * Get badge content for a comic, combining issue number with variant description if present
 * @param {number} issueNumber - The issue number
 * @param {string} variantDescription - The variant description (may be undefined or empty)
 * @returns {string} - The badge content string
 */
export const getBadgeContent = (issueNumber, variantDescription) => {
    return issueNumber + (variantDescription && variantDescription.length > 0 ? variantDescription.charAt(0) : '');
};

/**
 * Check if a comic is a variant
 * @param {object} comic - The comic object
 * @returns {boolean} - True if the comic is a variant
 */
export const isVariant = (comic) => {
    return !!(comic && comic.title && comic.title.includes(VARIANT_IDENTIFIER));
};

/**
 * Count the number of non-variant comics in an array
 * @param {Array} comics - Array of comic objects
 * @returns {number} - Count of non-variant comics
 */
export const countNonVariantComics = (comics) => {
    if (!comics || !Array.isArray(comics)) {
        return 0;
    }
    return comics.filter(comic => !isVariant(comic)).length;
};

/**
 * Get the appropriate Comic Vine image URL based on requested size
 * @param {object} comicVineImage - Comic Vine image object
 * @param {string} size - Requested size (portrait_medium, portrait_fantastic, etc.)
 * @returns {string} - Image URL
 */
const getComicVineImageUrl = (comicVineImage, size) => {
    // If it's just a URL string (legacy format), return it as-is
    if (typeof comicVineImage === 'string') {
        return comicVineImage;
    }
    
    // If it's not an object, return null
    if (typeof comicVineImage !== 'object' || comicVineImage === null) {
        return null;
    }
    
    // Map requested size to Comic Vine image sizes
    let preferredUrl = null;
    switch (size) {
        case 'portrait_medium':
            // Use smaller sizes for medium
            preferredUrl = comicVineImage.small_url || comicVineImage.medium_url;
            break;
        case 'portrait_fantastic':
            // Use small/medium for fantastic
            preferredUrl = comicVineImage.small_url || comicVineImage.medium_url;
            break;
        case 'portrait_xlarge':
            // Use medium/screen for xlarge
            preferredUrl = comicVineImage.medium_url || comicVineImage.screen_url;
            break;
        default:
            // Default to medium if available
            preferredUrl = comicVineImage.medium_url || comicVineImage.small_url;
    }
    
    // Final fallback if preferred size not available
    return preferredUrl || comicVineImage.medium_url || comicVineImage.small_url || null;
};

/**
 * Get the image URL for a comic object
 * Defaults to Comic Vine image if available, falls back to Marvel image
 * Comic Vine images are selected from the image object based on requested size
 * @param {object} item - The comic/series/hero object
 * @param {string} size - Size suffix for Marvel images (e.g., 'portrait_fantastic', 'portrait_medium')
 * @returns {string} - The image URL
 */
export const getImageUrl = (item, size = 'portrait_fantastic') => {
    // Use Comic Vine image if available
    if (item.comicVineImage) {
        const url = getComicVineImageUrl(item.comicVineImage, size);
        if (url) {
            return url;
        }
    }
    
    // Fall back to Marvel image format
    if (item.thumbnail) {
        // Handle case where thumbnail.path might be an object (comicVineImage) instead of a string
        const path = item.thumbnail.path;
        if (typeof path === 'string' && path.length > 0) {
            const securePath = path.replace('http', 'https');
            const extension = item.thumbnail.extension;
            return `${securePath}/${size}.${extension}`;
        } else if (typeof path === 'object' && path !== null) {
            // If path is a Comic Vine image object, extract the URL
            const url = getComicVineImageUrl(path, size);
            if (url) {
                return url;
            }
        }
    }
    
    // Default placeholder
    return 'http://i.annihil.us/u/prod/marvel/i/mg/b/40/image_not_available';
};

/**
 * Strip parenthetical content from the end of a series title for better search matching
 * Handles patterns like "Captain America (1968)" -> "Captain America"
 * Also handles "Spider-Man (2021-2022)" -> "Spider-Man"
 * Strips any content in parentheses at the end of the title
 * @param {string} title - The series title
 * @returns {string} - The title with parenthetical content removed
 */
export const stripYearFromTitle = (title) => {
    if (typeof title !== 'string') {
        return '';
    }
    // Match any content in parentheses at end of string, with optional whitespace
    return title.replace(/\s*\([^)]*\)\s*$/, '').trim();
};

/**
 * Find gap issues - unowned comics where there is both a lower and higher issue number that is owned
 * @param {Array} allComicsInSeries - Array of all comics in a series
 * @param {Set|Array} ownedComics - Set of owned comic IDs or Array of ownership records
 * @returns {Array} - Array of gap issue comics
 */
export const findGapIssues = (allComicsInSeries, ownedComics) => {
    if (!allComicsInSeries || !Array.isArray(allComicsInSeries) || !ownedComics) {
        return [];
    }

    // Group comics by series to process each series separately
    const comicsBySeries = {};
    allComicsInSeries.forEach(comic => {
        const seriesName = comic.series?.name || 'unknown';
        if (!comicsBySeries[seriesName]) {
            comicsBySeries[seriesName] = [];
        }
        comicsBySeries[seriesName].push(comic);
    });

    const gapIssues = [];

    // Process each series independently
    Object.values(comicsBySeries).forEach(seriesComics => {
        // Separate owned and unowned comics using isComicOwned for proper ID matching
        const ownedIssues = seriesComics
            .filter(comic => isComicOwned(comic, ownedComics))
            .map(comic => comic.issueNumber)
            .filter(num => num !== undefined && num !== null);
        
        const unownedComics = seriesComics.filter(comic => !isComicOwned(comic, ownedComics));

        if (ownedIssues.length < 2) {
            // Need at least 2 owned issues to have gaps
            return;
        }

        const minOwnedIssue = Math.min(...ownedIssues);
        const maxOwnedIssue = Math.max(...ownedIssues);

        // Find unowned comics that fall between owned issues
        unownedComics.forEach(comic => {
            const issueNum = comic.issueNumber;
            if (issueNum !== undefined && issueNum !== null && 
                issueNum > minOwnedIssue && issueNum < maxOwnedIssue) {
                gapIssues.push(comic);
            }
        });
    });

    return gapIssues;
};

/**
 * Check if a comic is owned by comparing both id and comicVineId
 * This handles backward compatibility where:
 * - Old Marvel comics only have `id` (Marvel ID)
 * - New Comic Vine comics have both `id` (which is comicVineId) and `comicVineId`
 * - Ownership records may have `id`, `comicVineId`, or both
 * 
 * @param {object} comic - The comic object with id and optional comicVineId
 * @param {Array|Set} ownedComics - Array of ownership records or Set of owned IDs
 * @returns {boolean} - True if the comic is owned
 */
export const isComicOwned = (comic, ownedComics) => {
    if (!comic || !ownedComics) {
        return false;
    }
    
    // Handle both Set and Array of ownership records
    if (ownedComics instanceof Set) {
        // Legacy mode: Set of IDs
        // Check if comic's id OR comicVineId is in the set
        if (ownedComics.has(comic.id)) {
            return true;
        }
        if (comic.comicVineId && ownedComics.has(comic.comicVineId)) {
            return true;
        }
        return false;
    }
    
    // Array of ownership records with id and optional comicVineId
    return ownedComics.some(owned => {
        // Check if the owned record's id matches comic's id
        if (owned.id === comic.id) {
            return true;
        }
        // Check if the owned record's comicVineId matches comic's comicVineId
        if (comic.comicVineId && owned.comicVineId && owned.comicVineId === comic.comicVineId) {
            return true;
        }
        // Check if the owned record's comicVineId matches comic's id (Comic Vine sourced comic)
        if (owned.comicVineId && owned.comicVineId === comic.id) {
            return true;
        }
        // Check if the owned record's id matches comic's comicVineId
        if (comic.comicVineId && owned.id === comic.comicVineId) {
            return true;
        }
        return false;
    });
};

/**
 * Create an ownership record for a comic
 * Stores both the original id and comicVineId for proper matching
 * 
 * @param {object} comic - The comic object
 * @param {number} seriesId - The series ID
 * @param {number} heroId - The hero ID
 * @returns {object} - Ownership record with id, comicVineId (if available), seriesId, and heroId
 */
export const createOwnershipRecord = (comic, seriesId, heroId) => {
    const record = {
        id: comic.id,
        seriesId: seriesId,
        heroId: heroId,
        modified: new Date().toISOString()
    };
    
    // Add comicVineId if available
    if (comic.comicVineId) {
        record.comicVineId = comic.comicVineId;
    }
    
    return record;
};

/**
 * Check if two comics refer to the same issue
 * Matches by id or comicVineId to handle cases where the same comic may have different IDs
 * 
 * @param {object} comic1 - First comic object
 * @param {object} comic2 - Second comic object
 * @returns {boolean} - True if the comics are the same
 */
export const isSameComic = (comic1, comic2) => {
    if (!comic1 || !comic2) {
        return false;
    }
    
    // Direct ID match
    if (comic1.id === comic2.id) {
        return true;
    }
    // Check if either comic's comicVineId matches the other's id
    if (comic1.comicVineId && comic1.comicVineId === comic2.id) {
        return true;
    }
    if (comic2.comicVineId && comic2.comicVineId === comic1.id) {
        return true;
    }
    // Check if both have comicVineIds and they match
    if (comic1.comicVineId && comic2.comicVineId && comic1.comicVineId === comic2.comicVineId) {
        return true;
    }
    return false;
};

/**
 * Filter out ownership records that match a comic (for removal)
 * Matches by id or comicVineId to handle both old Marvel and new Comic Vine data
 * 
 * @param {Array} ownedComics - Array of ownership records
 * @param {object} comic - The comic to remove
 * @returns {Array} - Filtered array without matching records
 */
export const filterOutOwnedComic = (ownedComics, comic) => {
    if (!ownedComics || !comic) {
        return ownedComics || [];
    }
    
    return ownedComics.filter(owned => {
        // Keep records that don't match the comic
        // Match if any ID matches
        if (owned.id === comic.id) {
            return false;
        }
        if (comic.comicVineId && owned.comicVineId && owned.comicVineId === comic.comicVineId) {
            return false;
        }
        if (owned.comicVineId && owned.comicVineId === comic.id) {
            return false;
        }
        if (comic.comicVineId && owned.id === comic.comicVineId) {
            return false;
        }
        return true;
    });
};
